import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Cube, Path, Broadcast, Stack,
  Warehouse, Truck, ChartLineUp, Target, CheckCircle, Circle,
} from "@phosphor-icons/react";
import SEO from "../components/SEO";
import Layout from "../components/Layout";
import Marquee from "../components/Marquee";
import ClientLogos from "../components/ClientLogos";
import { Reveal, Stagger, StaggerItem, LineReveal } from "../components/motion";
import { StatusBadge, Placeholder } from "../components/StatusBadge";
import {
  OUTCOMES, INDUSTRIES, DIFFERENT, FACTORY_FLOW, OS_MODULES,
} from "../data/site";
import { IMAGES } from "../data/images";
import { useCopy } from "../context/MessagingContext";

// Renders a title from copy segments (with optional yellow accent)
function Seg({ parts, accentClass = "text-ez-yellow" }) {
  return (
    <>
      {parts.map((p, i) => (p.a ? <span key={i} className={accentClass}>{p.t}</span> : <span key={i}>{p.t}</span>))}
    </>
  );
}

const IMG = {
  hero: IMAGES.coldWarehouseCyan,
  factory: IMAGES.forkliftYellow,
  warehouse: IMAGES.coldRacks,
  os: IMAGES.coldWarehouse,
  eazyms: IMAGES.frozenStacks,
  reefer: IMAGES.reeferTruck,
};

/* ---------------- HERO ---------------- */
function Hero() {
  const c = useCopy();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const heroLines = c.hero_lines.map((segs, i) => <Seg key={i} parts={segs} />);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[92vh] bg-os-bg text-white overflow-hidden grain flex flex-col">
      <motion.div style={{ scale, y }} className="absolute inset-0 opacity-40">
        <img src={IMG.hero} alt="Supply chain execution" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-os-bg/70 via-os-bg/40 to-os-bg" />
      </motion.div>
      <div className="absolute inset-0 grid-bg-dark opacity-30" />

      <div className="relative flex-1 max-w-[1600px] mx-auto w-full px-5 md:px-8 flex flex-col justify-center pt-24 pb-12">
        <motion.div
          key={c.hero_eyebrow}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8 font-mono-ez text-xs uppercase tracking-[0.3em] text-ez-yellow"
        >
          <span className="w-8 h-px bg-ez-yellow" />
          {c.hero_eyebrow}
        </motion.div>

        <h1 className="font-display uppercase tracking-tighter leading-[0.86] text-[13vw] md:text-[10vw] lg:text-[8.4vw]">
          <LineReveal key={c.label} lines={heroLines} delay={0.15} />
        </h1>

        <motion.p
          key={c.hero_paragraph}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          {c.hero_paragraph}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link to="/contact" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-ez-yellow text-ez-text px-7 py-4 font-semibold hover:bg-white transition-colors">
            {c.hero_cta_primary} <ArrowUpRight size={18} weight="bold" />
          </Link>
          <Link to="/eazymuv-os" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 border border-white/30 px-7 py-4 font-semibold hover:bg-white/10 transition-colors">
            {c.hero_cta_secondary} <ArrowRight size={18} weight="bold" />
          </Link>
        </motion.div>
      </div>

      <div className="relative border-t border-white/10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
        {c.hero_tiles.map(([a, b], i) => (
          <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 + i * 0.1 }} className="px-5 md:px-8 py-6">
            <div className="font-display text-lg md:text-2xl uppercase tracking-tight">{a}</div>
            <div className="text-white/50 text-xs md:text-sm font-mono-ez">{b}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- SECTION HEADER ---------------- */
function ChapterHead({ n, eyebrow, title, className = "" }) {
  return (
    <div className={className}>
      <div className="flex items-center gap-3 font-mono-ez text-xs uppercase tracking-[0.3em] text-ez-text/50 mb-6">
        <span className="text-ez-text/30">{n}</span>
        <span className="w-8 h-px bg-ez-text/30" />
        {eyebrow}
      </div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter leading-[0.95] max-w-3xl">
        {title}
      </h2>
    </div>
  );
}

/* ---------------- EXECUTION GAP ---------------- */
function ExecutionGap() {
  const c = useCopy();
  return (
    <section data-testid="execution-gap" className="relative bg-ez-bg border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal><ChapterHead n="01" eyebrow={c.gap_eyebrow} title={<Seg parts={c.gap_title} accentClass="text-ez-text/40" />} /></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
          <Reveal className="lg:col-span-5">
            <p className="text-lg leading-relaxed text-ez-text/80">{c.gap_para1}</p>
            <p className="mt-6 text-lg leading-relaxed text-ez-text/80">{c.gap_para2}</p>
          </Reveal>
          <div className="lg:col-span-7">
            <Stagger className="grid grid-cols-1 sm:grid-cols-3 border border-ez-text divide-y sm:divide-y-0 sm:divide-x divide-ez-text">
              {c.gap_cards.map((card, i) => (
                <StaggerItem key={i} className="p-8 group hover:bg-ez-yellow transition-colors">
                  <div className="font-mono-ez text-xs text-ez-text/40 mb-6">GAP·0{i + 1}</div>
                  <div className="font-display text-xl uppercase tracking-tight leading-tight">{card.t}</div>
                  <p className="mt-3 text-sm text-ez-text/70">{card.d}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- OPERATING MODEL ---------------- */
function OperatingModel() {
  const c = useCopy();
  const icons = [Warehouse, Broadcast, Truck];
  return (
    <section data-testid="operating-model" className="bg-white border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal><ChapterHead n="02" eyebrow={c.model_eyebrow} title={<Seg parts={c.model_title} />} /></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg leading-relaxed text-ez-text/80 max-w-xl">{c.model_para}</p>
            </Reveal>
            <Stagger className="mt-10 space-y-px bg-ez-border border border-ez-text">
              {c.model_rows.map((r, idx) => {
                const Icon = icons[idx];
                return (
                  <StaggerItem key={idx} className="bg-white flex gap-5 p-6 hover:bg-ez-bg transition-colors">
                    <Icon size={28} weight="duotone" className="text-ez-text shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold">{r.t}</div>
                      <div className="text-sm text-ez-text/60 mt-1">{r.d}</div>
                    </div>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>
          <Reveal className="lg:col-span-6" delay={0.15}>
            <div className="relative border border-ez-text">
              <img src={IMG.factory} alt="Factory floor operations" className="w-full h-[520px] object-cover" loading="lazy" />
              <div className="absolute top-0 left-0 bg-ez-yellow text-ez-text px-4 py-2 font-mono-ez text-xs uppercase tracking-widest">
                Operating Model / Floor
              </div>
              <div className="absolute bottom-4 right-4"><Placeholder label="Operational photography" /></div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- EAZYMUV OS (DARK) ---------------- */
function EazymuvOS() {
  const c = useCopy();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  return (
    <section ref={ref} data-testid="eazymuv-os-section" className="relative bg-os-bg text-white overflow-hidden grain border-b border-white/10">
      <motion.div style={{ y: bgY }} className="absolute inset-0 opacity-20">
        <img src={IMG.os} alt="" className="w-full h-full object-cover" loading="lazy" />
      </motion.div>
      <div className="absolute inset-0 grid-bg-dark opacity-40" />
      <div className="relative max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal>
          <div className="flex items-center gap-3 font-mono-ez text-xs uppercase tracking-[0.3em] text-ez-yellow mb-6">
            <span>03</span><span className="w-8 h-px bg-ez-yellow" /> {c.os_eyebrow}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl uppercase tracking-tighter leading-[0.9] max-w-4xl">
            <Seg parts={c.os_title} />
          </h2>
          <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">{c.os_para}</p>
        </Reveal>

        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-white/15 divide-y md:divide-y-0 md:divide-x divide-white/15">
          {OS_MODULES.map((m, i) => (
            <StaggerItem key={i} className="p-8 group hover:bg-white/[0.04] transition-colors relative">
              <div className="flex justify-between items-start mb-8">
                <span className="font-mono-ez text-xs text-white/30">OS·0{i + 1}</span>
                <StatusBadge status={m.status} />
              </div>
              {[Broadcast, Stack, Path, ChartLineUp][i] &&
                (() => {
                  const Icon = [Broadcast, Stack, Path, ChartLineUp][i];
                  return <Icon size={32} weight="duotone" className="text-ez-yellow mb-6" />;
                })()}
              <div className="font-display text-2xl uppercase tracking-tight">{m.name}</div>
              <p className="mt-3 text-sm text-white/50">{m.desc}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- PRODUCT ECOSYSTEM ---------------- */
function ProductEcosystem() {
  const c = useCopy();
  const products = [
    {
      name: "EazyMS", to: "/eazyms", theme: "ms",
      tag: c.product_eazyms_tag, img: IMG.eazyms,
      desc: c.product_eazyms_desc,
      status: "Live",
    },
    {
      name: "EazyOS", to: "/eazymuv-os", theme: "os",
      tag: "Operating System · 100% Visibility", img: IMG.os,
      desc: "The Eazymuv Operating System — a single operating view across factory, warehouse and logistics.",
      status: "In deployment",
    },
  ];
  return (
    <section data-testid="product-ecosystem" className="bg-ez-bg border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal><ChapterHead n="04" eyebrow="Product Ecosystem" title={<Seg parts={c.product_title} />} /></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-ez-text border border-ez-text mt-14">
          {products.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <Link to={p.to} data-testid={`product-card-${p.name.toLowerCase()}`} className="group block bg-white h-full">
                <div className="relative h-64 overflow-hidden">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className={`absolute inset-0 ${p.theme === "zoom" ? "bg-zoom-teal/30" : "bg-ms-yellow/10"}`} />
                  <div className="absolute top-4 left-4"><StatusBadge status={p.status} /></div>
                </div>
                <div className="p-8">
                  <div className="font-mono-ez text-xs uppercase tracking-widest text-ez-text/50 mb-3">{p.tag}</div>
                  <div className="flex items-center justify-between">
                    <span className={`font-display text-4xl uppercase tracking-tighter ${p.theme === "zoom" ? "text-zoom-teal" : "text-ez-text"}`}>{p.name}</span>
                    <ArrowUpRight size={28} weight="bold" className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                  <p className="mt-4 text-ez-text/70 leading-relaxed">{p.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- CUSTOMER OUTCOMES ---------------- */
function Outcomes() {
  const c = useCopy();
  return (
    <section data-testid="customer-outcomes" className="bg-white border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal><ChapterHead n="05" eyebrow="Customer Outcomes" title={<Seg parts={c.outcomes_title} />} /></Reveal>
        <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ez-border border border-ez-text mt-14">
          {c.outcomes_items.map((o, i) => (
            <StaggerItem key={i} className="bg-white p-10">
              <div className="font-display text-6xl lg:text-7xl uppercase tracking-tighter text-ez-yellow" style={{ WebkitTextStroke: "1px #0A0A0A" }}>
                {o.k}
              </div>
              <div className="font-display text-xl uppercase tracking-tight mt-4">{o.label}</div>
              <p className="mt-3 text-sm text-ez-text/70">{o.note}</p>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.2}>
          <div className="mt-8 flex items-center gap-3 text-sm text-ez-text/50">
            <Placeholder label="Metric to be verified" />
            Quantified outcomes are published only once verified and customer-approved.
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- HOW WE'RE DIFFERENT ---------------- */
function Different() {
  const c = useCopy();
  return (
    <section data-testid="how-different" className="bg-ez-bg border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal><ChapterHead n="06" eyebrow="How We're Different" title={<Seg parts={c.different_title} accentClass="text-ez-yellow" />} /></Reveal>
        <Reveal delay={0.1}>
          {/* Mobile: stacked cards */}
          <div className="mt-12 space-y-4 md:hidden">
            {DIFFERENT.map((row, i) => (
              <div key={i} className="border border-ez-text bg-white">
                <div className="bg-ez-text text-ez-bg px-4 py-2 font-mono-ez text-xs uppercase tracking-widest">{row.dim}</div>
                <div className="p-4 border-b border-ez-border">
                  <div className="font-mono-ez text-[10px] uppercase tracking-widest text-ez-text/40 mb-1">Traditional 3PL / SaaS</div>
                  <div className="text-sm text-ez-text/50 line-through decoration-ez-text/30 break-words">{row.them}</div>
                </div>
                <div className="p-4 bg-ez-yellow/10">
                  <div className="font-mono-ez text-[10px] uppercase tracking-widest text-ez-text/60 mb-1">Eazymuv</div>
                  <div className="text-sm font-medium flex items-start gap-2 break-words">
                    <CheckCircle size={16} weight="fill" className="text-ez-yellow shrink-0 mt-0.5" />
                    {row.us}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop: comparison table */}
          <div className="mt-14 border border-ez-text bg-white overflow-hidden hidden md:block">
            <div className="grid grid-cols-3 border-b border-ez-text bg-ez-text text-ez-bg font-mono-ez text-xs uppercase tracking-widest">
              <div className="p-4">Dimension</div>
              <div className="p-4 border-l border-white/20">Traditional 3PL / SaaS</div>
              <div className="p-4 border-l border-ez-yellow bg-ez-yellow text-ez-text">Eazymuv</div>
            </div>
            {DIFFERENT.map((row, i) => (
              <div key={i} className="grid grid-cols-3 border-b border-ez-border last:border-b-0 text-sm">
                <div className="p-5 font-semibold">{row.dim}</div>
                <div className="p-5 border-l border-ez-border text-ez-text/50 line-through decoration-ez-text/30">{row.them}</div>
                <div className="p-5 border-l border-ez-text font-medium flex items-start gap-2">
                  <CheckCircle size={18} weight="fill" className="text-ez-yellow shrink-0 mt-0.5" />
                  {row.us}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- START INSIDE THE FACTORY ---------------- */
function FactoryFlow() {
  const c = useCopy();
  return (
    <section data-testid="factory-flow" className="bg-os-bg text-white grain border-b border-white/10 relative">
      <div className="absolute inset-0 grid-bg-dark opacity-30" />
      <div className="relative max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal>
          <div className="flex items-center gap-3 font-mono-ez text-xs uppercase tracking-[0.3em] text-ez-yellow mb-6">
            <span>07</span><span className="w-8 h-px bg-ez-yellow" /> {c.factory_eyebrow}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.92] max-w-4xl">
            {c.factory_title}
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-5 border border-white/15 divide-y md:divide-y-0 md:divide-x divide-white/15">
          {FACTORY_FLOW.map((s) => (
            <StaggerItem key={s.n} className="p-6 md:p-8 group hover:bg-ez-yellow hover:text-ez-text transition-colors">
              <div className="font-display text-5xl tracking-tighter text-ez-yellow group-hover:text-ez-text">{s.n}</div>
              <div className="font-semibold mt-5 leading-tight">{s.t}</div>
              <p className="mt-3 text-sm text-white/50 group-hover:text-ez-text/70">{s.d}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- LAND & EXPAND ---------------- */
function LandExpand() {
  const c = useCopy();
  return (
    <section data-testid="land-expand" className="bg-white border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <div className="relative border border-ez-text">
            <img src={IMG.warehouse} alt="Warehouse interior" className="w-full h-[480px] object-cover" loading="lazy" />
            <div className="absolute inset-0 grid-bg opacity-30" />
            <div className="absolute bottom-4 left-4 bg-ez-text text-ez-bg px-4 py-2 font-mono-ez text-xs uppercase tracking-widest">
              Land → Expand
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-6">
          <Reveal><ChapterHead n="08" eyebrow="Land and Expand" title={<Seg parts={c.land_title} />} /></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg leading-relaxed text-ez-text/80 max-w-xl">{c.land_para}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/how-we-work" data-testid="land-expand-cta" className="mt-8 inline-flex items-center gap-2 bg-ez-text text-ez-bg px-7 py-4 font-semibold hover:bg-ez-yellow hover:text-ez-text transition-colors">
              See How We Work <ArrowRight size={18} weight="bold" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- OPERATING PROOF ---------------- */
function OperatingProof() {
  const cp = useCopy();
  return (
    <section data-testid="operating-proof" className="bg-ez-bg border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal><ChapterHead n="09" eyebrow="Operating Proof" title={<Seg parts={cp.proof_title} />} /></Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-ez-text/70 leading-relaxed">{cp.proof_para}</p>
        </Reveal>
        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-ez-border border border-ez-text">
          {[
            { t: "Managed Operations", d: "Customer references — display pending permission.", label: "Customer approval required" },
            { t: "Customer References", d: "Named references, shown only with permission.", label: "Customer approval required" },
            { t: "Case Studies", d: "Verified operating outcomes, published on approval.", label: "Placeholder" },
          ].map((c, i) => (
            <StaggerItem key={i} className="bg-white p-8">
              <Target size={28} weight="duotone" className="text-ez-text mb-6" />
              <div className="font-display text-xl uppercase tracking-tight">{c.t}</div>
              <p className="mt-3 text-sm text-ez-text/60">{c.d}</p>
              <div className="mt-5"><Placeholder label={c.label} /></div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- INDUSTRIES ---------------- */
function IndustriesSection() {
  const c = useCopy();
  return (
    <section data-testid="industries-section" className="bg-white border-b border-ez-text">
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <Reveal><ChapterHead n="10" eyebrow="Industries" title={<Seg parts={c.industries_title} />} /></Reveal>
        <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 border border-ez-text">
          {INDUSTRIES.map((ind, i) => (
            <StaggerItem key={i} className="group border-b border-r border-ez-border p-6 sm:p-8 hover:bg-ez-text hover:text-ez-bg transition-colors">
              <Cube size={24} weight="duotone" className="text-ez-yellow mb-6" />
              <div className="font-display text-xl sm:text-2xl uppercase tracking-tight leading-tight break-words">{ind.name}</div>
              <p className="mt-3 text-sm opacity-60">{ind.note}</p>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.1}>
          <Link to="/industries" data-testid="industries-cta" className="mt-8 inline-flex items-center gap-2 font-semibold border-b-2 border-ez-yellow pb-1 hover:gap-3 transition-all">
            Explore all industries <ArrowRight size={16} weight="bold" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCTA() {
  const c = useCopy();
  return (
    <section data-testid="final-cta" className="bg-ez-yellow text-ez-text relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative max-w-[1600px] mx-auto px-5 md:px-8 py-24 md:py-36 text-center flex flex-col items-center">
        <div className="font-mono-ez text-xs uppercase tracking-[0.3em] mb-8">{c.final_eyebrow}</div>
        <h2 className="font-display text-4xl sm:text-6xl lg:text-[7vw] uppercase tracking-tighter leading-[0.88] max-w-5xl">
          {c.final_title}
        </h2>
        <p className="mt-8 max-w-xl text-lg text-ez-text/70">{c.final_para}</p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/contact" data-testid="final-cta-primary" className="inline-flex items-center gap-2 bg-ez-text text-ez-bg px-8 py-4 font-semibold hover:bg-white hover:text-ez-text transition-colors">
            {c.final_cta_primary} <ArrowUpRight size={18} weight="bold" />
          </Link>
          <Link to="/eazyms" data-testid="final-cta-secondary" className="inline-flex items-center gap-2 border-2 border-ez-text px-8 py-4 font-semibold hover:bg-ez-text hover:text-ez-bg transition-colors">
            {c.final_cta_secondary}
          </Link>
        </div>
      </div>
    </section>
  );
}

function HomeContent() {
  const c = useCopy();
  return (
    <Layout>
      <SEO title="Supply Chain Execution" description="Eazymuv is a technology-enabled supply chain execution company. One accountable operating partner across factory, warehouse and logistics — instrumented by Eazymuv OS." image={IMG.hero} />
      <Hero />
      <Marquee items={c.marquee} className="bg-ez-text text-ez-bg py-6 border-b border-ez-text" dark />
      <ExecutionGap />
      <OperatingModel />
      <EazymuvOS />
      <ProductEcosystem />
      <Outcomes />
      <Different />
      <FactoryFlow />
      <LandExpand />
      <OperatingProof />
      <ClientLogos />
      <IndustriesSection />
      <FinalCTA />
    </Layout>
  );
}

export default function Home() {
  return <HomeContent />;
}
